import * as z from 'zod/mini';
import {
  IDENTIFIER_MAX_LENGTH,
  IDENTIFIER_PAD_DEFAULT,
  IDENTIFIER_PAD_MAX,
  IDENTIFIER_PAD_MIN,
  IDENTIFIER_PREFIX_MAX_LENGTH,
  IDENTIFIER_SEPARATOR_DEFAULT,
  IDENTIFIER_SEPARATOR_MAX_LENGTH,
} from './constants.ts';
import { ITEM_TYPES } from './item-types.ts';
import type { PathEntry } from './schemas/common.ts';

export const NAMING_TYPES = ['client', 'property', 'room', 'rack', 'drop', ...ITEM_TYPES] as const;
export type NamingType = (typeof NAMING_TYPES)[number];

// Every node has a short path segment, separate from its display name and UUID.
// Prefix settings apply to future generated IDs; SQLite allocates in the parent
// namespace inside the write transaction. Existing names and IDs stay intact.

/** An identifier input: trimmed and bounded; blank requests server allocation. */
export const identifierSchema = z
  .string()
  .check(
    z.trim(),
    z.maxLength(IDENTIFIER_MAX_LENGTH),
    z.regex(
      /^[A-Za-z0-9_-]*$/u,
      'Use letters, numbers, hyphens or underscores; no spaces or slashes',
    ),
  );

/** The default ID prefix for each item type; overridable in the scheme. */
export const DEFAULT_ID_PREFIXES: Readonly<Record<NamingType, string>> = {
  client: 'CL',
  property: 'PR',
  room: 'RM',
  rack: 'RK',
  drop: 'DR',
  access_point: 'AP',
  router: 'RTR',
  firewall: 'FW',
  switch: 'SW',
  camera: 'CAM',
  nvr: 'NVR',
  ups: 'UPS',
  pdu: 'PDU',
  modem_ont: 'MDM',
  media_converter: 'MC',
  splitter: 'SPL',
  controller: 'CTL',
  server_nas: 'SRV',
  computer: 'PC',
  laptop: 'LT',
  screen: 'SCR',
  printer_3d: 'P3D',
  access_control_panel: 'ACP',
  reader: 'RDR',
  door_strike: 'STK',
  intrusion_panel: 'IDS',
  fire_panel: 'FACP',
  horn_strobe: 'HS',
  amplifier: 'AMP',
  receiver: 'RCV',
  projector: 'PRJ',
  speaker: 'SPK',
  keypad: 'KP',
  thermostat: 'TSTAT',
  sensor: 'SEN',
  phone: 'PH',
  printer: 'PRN',
  wall_patch_panel: 'WPP',
  outlet: 'TO',
  other: 'DEV',
  patch_panel: 'PP',
  blank_panel: 'BLK',
  shelf: 'SHF',
  drawer: 'DRW',
  cable_manager: 'CM',
  power_strip: 'PS',
};

/** One per-type prefix override: an uppercase code with no spaces (E1). */
const prefixValueSchema = z
  .string()
  .check(
    z.minLength(1),
    z.maxLength(IDENTIFIER_PREFIX_MAX_LENGTH),
    z.regex(
      /^[A-Z][A-Z0-9_-]*$/u,
      'Start with an uppercase letter; use uppercase letters, numbers, hyphens or underscores',
    ),
  );

/**
 * Per-type prefix overrides, keyed by the item type string. Requests may supply only the types being changed; the repository resolves
 * the full default map before persistence so the SQL allocator has every key.
 */
export const prefixesSchema = z.partialRecord(z.enum(NAMING_TYPES), prefixValueSchema);

/** The account's naming scheme: the separator, the number width, uppercase enforcement, and the overrides. */
export const namingSchemeSchema = z.object({
  /** What sits between the prefix and the number (''/'-'); no spaces. */
  separator: z
    .string()
    .check(
      z.maxLength(IDENTIFIER_SEPARATOR_MAX_LENGTH),
      z.regex(/^[-_]*$/u, 'Use hyphens, underscores or no separator'),
    ),
  /** Digits the number is padded to. */
  pad: z
    .int()
    .check(
      z.gte(IDENTIFIER_PAD_MIN, `Use at least ${String(IDENTIFIER_PAD_MIN)} digit`),
      z.lte(IDENTIFIER_PAD_MAX, `Use at most ${String(IDENTIFIER_PAD_MAX)} digits`),
    ),
  /** Whether typed IDs must be uppercase. Path-safe characters always apply. */
  enforced: z.boolean(),
  prefixes: prefixesSchema,
});
export type NamingScheme = z.infer<typeof namingSchemeSchema>;

/** Default values used to initialize or reset the shared scheme. */
export const DEFAULT_NAMING_SCHEME: NamingScheme = {
  separator: IDENTIFIER_SEPARATOR_DEFAULT,
  pad: IDENTIFIER_PAD_DEFAULT,
  enforced: true,
  prefixes: {},
};

/** The scheme editor sends the whole config; every field defaults so a partial save is complete. */
export const namingSchemeInputSchema = z.object({
  separator: z._default(namingSchemeSchema.shape.separator, DEFAULT_NAMING_SCHEME.separator),
  pad: z._default(namingSchemeSchema.shape.pad, DEFAULT_NAMING_SCHEME.pad),
  enforced: z._default(namingSchemeSchema.shape.enforced, DEFAULT_NAMING_SCHEME.enforced),
  prefixes: z._default(prefixesSchema, {}),
});
export type NamingSchemeInput = z.infer<typeof namingSchemeInputSchema>;

/** The ID prefix a type uses: the scheme's override when it has one, else the default. */
export function prefixFor(scheme: NamingScheme, type: NamingType): string {
  return scheme.prefixes[type] ?? DEFAULT_ID_PREFIXES[type];
}

/** A formatted identifier for a type and a number: prefix, separator, then the padded number. */
export function formatIdentifier(scheme: NamingScheme, type: NamingType, n: number): string {
  return `${prefixFor(scheme, type)}${scheme.separator}${String(n).padStart(scheme.pad, '0')}`;
}

/** UUID routing stays separate from this readable resource/port path. */
export function identifierPath(path: readonly PathEntry[], port?: number): string {
  const segments: string[] = [];
  for (const [index, entry] of path.entries()) {
    // Ports, checklists and reports append a non-node breadcrumb with the same UUID.
    if (entry.identifier === undefined && index > 0 && path[index - 1]?.id === entry.id) {
      continue;
    }
    segments.push(
      entry.identifier === undefined || entry.identifier === ''
        ? '[ID pending sync]'
        : entry.identifier,
    );
    if (entry.port !== undefined) {
      segments.push(String(entry.port));
    }
  }
  if (port !== undefined) {
    segments.push(String(port));
  }
  return segments.join('/');
}
